import React, { ReactElement, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Button,
  makeStyles,
  Tab,
  Tabs,
} from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { useHistory, useLocation } from "react-router-dom";
import { LineChart } from "@mui/x-charts/LineChart";

import theme from "../../../themes";
import { useEnumStrings } from "../../enumStrings";
import ExportButton from "../../common/ExportButton";
import VideoTutorialButton from "../../common/VideoTutorialButton";
import Loader from "../../common/Loader/Loader";
import {
  AffinityProgramPartnerInquiryEmailDeliveryStatusEnum,
  useGetAffinityProgramPartnerQuery,
  useGetAffinityProgramPartnerStatsQuery,
} from "../../../graphql/types";
import {
  AffinityProgramStatsLocationState,
  getAffinityStatsRouteParams,
  mergeAffinityInquiryPages,
  sortAffinityInquiriesNewestFirst,
} from "./affinityProgramStatsRoute";

const useStyles = makeStyles(() => ({
  pageRoot: {
    overflowX: "hidden",
  },
  headerSection: {
    backgroundColor: theme.palette.primary.main,
    padding: "30px 40px",
    minHeight: "200px",
    [theme.breakpoints.down("sm")]: {
      padding: "16px 16px",
      minHeight: "auto",
    },
  },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    marginBottom: "10px",
  },
  title: {
    margin: 0,
    fontSize: "32px",
    color: theme.palette.primary.contrastText,
    fontWeight: 700,
    fontFamily: theme.typography.fontFamily,
    [theme.breakpoints.down("sm")]: {
      fontSize: "24px",
    },
  },
  description: {
    fontSize: "18px",
    color: "rgba(255, 255, 255, 0.92)",
    marginTop: "8px",
    lineHeight: 1.55,
    maxWidth: "920px",
    fontFamily: theme.typography.fontFamily,
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
    },
  },
  buttonContainer: {
    display: "flex",
    gap: "15px",
    flexWrap: "wrap",
    marginTop: "20px",
  },
  contentContainer: {
    backgroundColor: "#D4A353",
    padding: "0 40px 40px 40px",
    overflowX: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: 0,
    },
  },
  content: {
    display: "flex",
    flexDirection: "column",
    background: theme.palette.background.paper,
    minHeight: "70vh",
    borderRadius: "20px",
    padding: "30px",
    gap: "26px",
    [theme.breakpoints.down("sm")]: {
      padding: "14px",
      borderRadius: 0,
    },
  },
  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: "16px",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  metricCard: {
    position: "relative" as const,
    border: "0.5px solid #E0E1E2",
    borderRadius: "20px",
    padding: "30px 28px 28px",
    display: "flex",
    flexDirection: "column",
    gap: "17px",
    backgroundColor: "#FFFFFF",
    minHeight: "381px",
    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
    fontFamily: "DM Sans, sans-serif",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "9px",
      backgroundColor: "#E1A731",
      borderTopLeftRadius: "20px",
      borderTopRightRadius: "20px",
    },
  },
  metricTitle: {
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 700,
    fontSize: "18px",
    lineHeight: "22px",
    color: "#000000",
    margin: 0,
    textTransform: "uppercase",
  },
  metricDesc: {
    fontFamily: "DM Sans, sans-serif",
    fontSize: "16px",
    fontWeight: 400,
    color: "#666666",
    lineHeight: "150%",
    margin: 0,
    minHeight: "72px",
  },
  metricValue: {
    fontFamily: "DM Sans, sans-serif",
    fontSize: "44px",
    fontWeight: 700,
    margin: 0,
    color: "#0E121D",
    lineHeight: "52px",
  },
  metricBreakdown: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    marginTop: "auto",
    paddingTop: "14px",
    borderTop: "1px solid #E3E4E9",
  },
  metricRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    fontFamily: "DM Sans, sans-serif",
    fontSize: "16px",
    fontWeight: 500,
    color: "#666666",
    lineHeight: "19px",
  },
  metricRowValue: {
    fontFamily: "DM Sans, sans-serif",
    fontSize: "20px",
    fontWeight: 500,
    color: "#0E121D",
    lineHeight: "24px",
  },
  metricRowGreen: {
    color: "#00B621",
    fontWeight: 500,
  },
  metricRowGreenValue: {
    fontFamily: "DM Sans, sans-serif",
    color: "#00B621",
    fontWeight: 700,
    fontSize: "20px",
    lineHeight: "24px",
  },
  conversionRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
  },
  conversionStack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "2px",
  },
  conversionLabel: {
    fontFamily: "DM Sans, sans-serif",
    color: "#00B621",
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "19px",
  },
  conversionValue: {
    fontFamily: "DM Sans, sans-serif",
    color: "#00B621",
    fontWeight: 700,
    fontSize: "20px",
    lineHeight: "24px",
  },
  chartCard: {
    border: "0.5px solid rgba(158, 158, 158, 0.43)",
    borderRadius: "20px",
    padding: "28px 32px 24px",
    backgroundColor: "#FFFFFF",
    boxShadow: "0px 2px 6px rgba(13, 10, 44, 0.08)",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    [theme.breakpoints.down("sm")]: {
      padding: "18px 16px",
      borderRadius: "16px",
    },
  },
  chartHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
  },
  chartTitle: {
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 700,
    fontSize: "22px",
    lineHeight: "28px",
    margin: 0,
    color: "#1E1B39",
  },
  chartDivider: {
    height: "1px",
    backgroundColor: "#E5E5EF",
    width: "100%",
  },
  chartControlsRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
  },
  legendsRow: {
    display: "flex",
    gap: "6px",
    flexWrap: "wrap",
  },
  legendChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 12px",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #E5E5EF",
    fontFamily: "DM Sans, sans-serif",
    fontSize: "12px",
    lineHeight: "14px",
    fontWeight: 400,
    color: "#1E1B39",
    cursor: "pointer",
    userSelect: "none" as const,
    transition: "opacity 120ms ease",
    "&:hover": { backgroundColor: "#FAFAFA" },
  },
  legendChipInactive: {
    opacity: 0.55,
  },
  legendCheck: {
    width: "14px",
    height: "14px",
    borderRadius: "2px",
    border: "1px solid #E5E5EF",
    backgroundColor: "#E5E5EF",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#000000",
    fontSize: "10px",
    fontWeight: 700,
    lineHeight: 1,
    boxSizing: "border-box",
  },
  legendCheckEmpty: {
    backgroundColor: "#FFFFFF",
    border: "1px solid #E5E5EF",
  },
  legendDot: {
    width: "9px",
    height: "9px",
    borderRadius: "50%",
  },
  rangeTabs: {
    minHeight: "44px",
    backgroundColor: "#FFFFFF",
    borderRadius: "15px",
    padding: "4px",
    border: "1px solid #E5E5EF",
    "& .MuiTabs-indicator": {
      display: "none",
    },
    "& .MuiTabs-flexContainer": {
      gap: "4px",
    },
    "& .MuiTab-root": {
      minHeight: "36px",
      minWidth: "auto",
      padding: "6px 22px",
      textTransform: "none",
      fontFamily: "DM Sans, sans-serif",
      fontWeight: 600,
      fontSize: "14px",
      color: "#9E9E9E",
      borderRadius: "10px",
      opacity: 1,
    },
    "& .Mui-selected": {
      backgroundColor: "#1A1F2E",
      color: "#FFFFFF !important",
      borderRadius: "10px",
    },
  },
  table: {
    width: "100%",
    minWidth: "900px",
    tableLayout: "fixed" as const,
    borderCollapse: "collapse" as const,
    fontFamily: "DM Sans, sans-serif",
    backgroundColor: "#FFFFFF",
  },
  tableWrap: {
    border: "1px solid #666666",
    borderRadius: "4px",
    overflow: "hidden",
    overflowX: "auto",
    backgroundColor: "#FFFFFF",
  },
  thGold: {
    backgroundColor: "#E1A731",
    color: "#FFFFFF",
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 600,
    fontSize: "18px",
    lineHeight: "130%",
    padding: "10px 12px",
    textAlign: "center" as const,
    border: "1px solid rgba(136, 136, 136, 0.5)",
    verticalAlign: "middle" as const,
  },
  thGoldGreen: {
    color: "#FFFFFF",
  },
  thSub: {
    backgroundColor: "#FFFFFF",
    color: "#24272C",
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "130%",
    padding: "10px 12px",
    textAlign: "center" as const,
    border: "1px solid rgba(136, 136, 136, 0.5)",
    verticalAlign: "middle" as const,
  },
  thSubGreen: {
    color: "#00B621",
    fontWeight: 600,
  },
  td: {
    padding: "10px 12px",
    fontFamily: "DM Sans, sans-serif",
    fontSize: "14px",
    lineHeight: "130%",
    fontWeight: 600,
    color: "#24272C",
    border: "1px solid rgba(136, 136, 136, 0.5)",
    textAlign: "center" as const,
    backgroundColor: "#FFFFFF",
  },
  tdMonth: {
    backgroundColor: "#FFFFFF",
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 400,
    fontSize: "18px",
    lineHeight: "150%",
    color: "#000000",
  },
  tdTotal: {
    backgroundColor: "#D9D9D9",
    fontWeight: 600,
    color: "#24272C",
  },
  tdGreen: {
    backgroundColor: "#FFFFFF",
    color: "#00B621",
    fontWeight: 600,
  },
  inquirySection: {
    border: "1px solid #E5E5EF",
    borderRadius: "16px",
    padding: "24px",
    backgroundColor: "#FFFFFF",
  },
  inquiryTitle: {
    margin: 0,
    color: "#24272C",
    fontSize: "22px",
    fontWeight: 700,
    fontFamily: "DM Sans, sans-serif",
  },
  inquiryDescription: {
    margin: "8px 0 20px",
    color: "#615E83",
    fontSize: "15px",
    lineHeight: 1.5,
    fontFamily: "DM Sans, sans-serif",
  },
  inquiryTable: {
    width: "100%",
    minWidth: "860px",
    borderCollapse: "collapse" as const,
    fontFamily: "DM Sans, sans-serif",
  },
  inquiryTh: {
    padding: "12px 14px",
    textAlign: "left" as const,
    color: "#FFFFFF",
    backgroundColor: "#E1A731",
    border: "1px solid rgba(136, 136, 136, 0.35)",
    fontSize: "14px",
    fontWeight: 700,
  },
  inquiryTd: {
    padding: "12px 14px",
    textAlign: "left" as const,
    color: "#24272C",
    backgroundColor: "#FFFFFF",
    border: "1px solid #E5E5EF",
    fontSize: "14px",
    verticalAlign: "middle" as const,
  },
  inquiryFooter: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "12px",
    justifyContent: "space-between",
    marginTop: "16px",
  },
  inquiryCount: {
    color: "#615E83",
    fontFamily: "DM Sans, sans-serif",
    fontSize: "14px",
    margin: 0,
  },
  inquiryLoadMoreError: {
    color: "#B42318",
    fontFamily: "DM Sans, sans-serif",
    fontSize: "14px",
    margin: "10px 0 0",
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: "999px",
    padding: "5px 10px",
    fontSize: "12px",
    fontWeight: 700,
    whiteSpace: "nowrap" as const,
  },
  statusSent: {
    color: "#087A2E",
    backgroundColor: "#E6F7EC",
  },
  statusPending: {
    color: "#8A5A00",
    backgroundColor: "#FFF4D6",
  },
  statusFailed: {
    color: "#B42318",
    backgroundColor: "#FEECEB",
  },
  emptyState: {
    padding: "28px 16px",
    textAlign: "center" as const,
    color: "#615E83",
    fontFamily: "DM Sans, sans-serif",
  },
  pageState: {
    minHeight: "60vh",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    padding: "32px",
    textAlign: "center" as const,
    color: "#24272C",
  },
  stateButton: {
    backgroundColor: "#E1A731",
    color: "#FFFFFF",
    fontWeight: 700,
    "&:hover": { backgroundColor: "#C89120" },
  },
}));

const COLORS = {
  views: "#4A3AFF",
  open: "#F97316",
  redirect: "#06B6D4",
};

type RangeTab = "months" | "year" | "inception";

type SeriesKey = "views" | "open" | "redirect";

const EMPTY_SERIES: Record<SeriesKey, number[]> = {
  views: [],
  open: [],
  redirect: [],
};

const INQUIRY_PAGE_SIZE = 100;

type TimeseriesPoint = {
  date: dayjs.Dayjs;
  views: number;
  open: number;
  redirect: number;
};

const parseTimeseriesPoints = (
  points:
    | ReadonlyArray<{
        monthAndYear: string;
        totalViewCount: number;
        totalOpenCount: number;
        totalRedirectCount: number;
      }>
    | null
    | undefined
): TimeseriesPoint[] => {
  if (!points || points.length === 0) return [];
  return points
    .map((p) => {
      const direct = dayjs(p.monthAndYear);
      const padded = dayjs(`${p.monthAndYear}-01`);
      const parsed = direct.isValid()
        ? direct
        : padded.isValid()
        ? padded
        : dayjs();
      return {
        date: parsed.startOf("month"),
        views: p.totalViewCount,
        open: p.totalOpenCount,
        redirect: p.totalRedirectCount,
      };
    })
    .sort((a, b) => a.date.valueOf() - b.date.valueOf());
};

const formatPercent = (value: number | null | undefined): string =>
  value === null || value === undefined ? "—" : `${value.toFixed(1)}%`;

const ViewAffinityProgramStats = (): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const history = useHistory();
  const location =
    useLocation<AffinityProgramStatsLocationState | undefined>();
  const routeParams = getAffinityStatsRouteParams(
    location.search,
    location.state
  );
  const programId = routeParams.programId;
  const fallbackProgramName = routeParams.programName;

  const {
    data: partnerData,
    loading: partnerLoading,
    error: partnerError,
    refetch: refetchPartner,
  } = useGetAffinityProgramPartnerQuery({
    variables: { id: programId },
    skip: !programId,
    fetchPolicy: "cache-and-network",
  });

  const {
    data: statsData,
    loading: statsLoading,
    error: statsError,
    refetch: refetchStats,
    fetchMore: fetchMoreStats,
  } = useGetAffinityProgramPartnerStatsQuery({
    variables: { id: programId, inquiryLimit: INQUIRY_PAGE_SIZE },
    skip: !programId,
    fetchPolicy: "cache-and-network",
  });

  const partner = partnerData?.getAffinityProgramPartner;
  const stats = statsData?.getAffinityProgramPartnerStats;
  const programName = partner?.companyName || fallbackProgramName;

  const inquiries = useMemo(
    () => sortAffinityInquiriesNewestFirst(stats?.inquiries || []),
    [stats?.inquiries]
  );

  const inquiryStatusLabel = (
    status: AffinityProgramPartnerInquiryEmailDeliveryStatusEnum
  ): string => {
    switch (status) {
      case AffinityProgramPartnerInquiryEmailDeliveryStatusEnum.Sent:
        return enumStrings.viewAffinityProgramStats.inquiryStatusSent;
      case AffinityProgramPartnerInquiryEmailDeliveryStatusEnum.Failed:
        return enumStrings.viewAffinityProgramStats.inquiryStatusFailed;
      case AffinityProgramPartnerInquiryEmailDeliveryStatusEnum.Pending:
      default:
        return enumStrings.viewAffinityProgramStats.inquiryStatusPending;
    }
  };

  const inquiryStatusClass = (
    status: AffinityProgramPartnerInquiryEmailDeliveryStatusEnum
  ): string => {
    switch (status) {
      case AffinityProgramPartnerInquiryEmailDeliveryStatusEnum.Sent:
        return classes.statusSent;
      case AffinityProgramPartnerInquiryEmailDeliveryStatusEnum.Failed:
        return classes.statusFailed;
      case AffinityProgramPartnerInquiryEmailDeliveryStatusEnum.Pending:
      default:
        return classes.statusPending;
    }
  };

  const allPoints = useMemo(
    () =>
      parseTimeseriesPoints(
        stats?.timeseriesData?.allChambers?.timeseriesData
      ),
    [stats]
  );

  const [range, setRange] = useState<RangeTab>("months");
  const [loadingMoreInquiries, setLoadingMoreInquiries] = useState(false);
  const [inquiryLoadMoreFailed, setInquiryLoadMoreFailed] = useState(false);
  const [visibleSeries, setVisibleSeries] = useState<Record<SeriesKey, boolean>>({
    views: true,
    open: true,
    redirect: true,
  });

  const toggleSeries = (key: SeriesKey) => {
    setVisibleSeries((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const loadMoreInquiries = async (): Promise<void> => {
    const inquiryNextToken = stats?.inquiriesNextToken;

    if (!inquiryNextToken || loadingMoreInquiries) {
      return;
    }

    setLoadingMoreInquiries(true);
    setInquiryLoadMoreFailed(false);

    try {
      await fetchMoreStats({
        variables: {
          id: programId,
          inquiryLimit: INQUIRY_PAGE_SIZE,
          inquiryNextToken,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return previousResult;
          }

          const previousStats =
            previousResult.getAffinityProgramPartnerStats;
          const nextStats = fetchMoreResult.getAffinityProgramPartnerStats;

          return {
            ...fetchMoreResult,
            getAffinityProgramPartnerStats: mergeAffinityInquiryPages({
              previousPage: previousStats,
              nextPage: nextStats,
            }),
          };
        },
      });
    } catch (_error) {
      setInquiryLoadMoreFailed(true);
    } finally {
      setLoadingMoreInquiries(false);
    }
  };

  const rangeData = useMemo(() => {
    if (allPoints.length === 0) {
      return { labels: [] as string[], series: EMPTY_SERIES };
    }

    if (range === "year") {
      const byYear = new Map<
        number,
        { views: number; open: number; redirect: number }
      >();
      allPoints.forEach((p) => {
        const y = p.date.year();
        const acc = byYear.get(y) || { views: 0, open: 0, redirect: 0 };
        acc.views += p.views;
        acc.open += p.open;
        acc.redirect += p.redirect;
        byYear.set(y, acc);
      });
      const years = Array.from(byYear.keys()).sort((a, b) => a - b);
      return {
        labels: years.map((y) => String(y)),
        series: {
          views: years.map((y) => byYear.get(y)!.views),
          open: years.map((y) => byYear.get(y)!.open),
          redirect: years.map((y) => byYear.get(y)!.redirect),
        },
      };
    }

    if (range === "inception") {
      return {
        labels: allPoints.map((p) => p.date.format("MMM YYYY").toUpperCase()),
        series: {
          views: allPoints.map((p) => p.views),
          open: allPoints.map((p) => p.open),
          redirect: allPoints.map((p) => p.redirect),
        },
      };
    }

    const last12 = allPoints.slice(-12);
    return {
      labels: last12.map((p) => p.date.format("MMM YYYY").toUpperCase()),
      series: {
        views: last12.map((p) => p.views),
        open: last12.map((p) => p.open),
        redirect: last12.map((p) => p.redirect),
      },
    };
  }, [range, allPoints]);

  const xLabels = rangeData.labels;
  const seriesData = rangeData.series;

  const seriesDefs: Array<{
    key: SeriesKey;
    label: string;
    color: string;
  }> = [
    {
      key: "views",
      label: enumStrings.viewAffinityProgramStats.legendViews,
      color: COLORS.views,
    },
    {
      key: "open",
      label: enumStrings.viewAffinityProgramStats.legendOpen,
      color: COLORS.open,
    },
    {
      key: "redirect",
      label: enumStrings.viewAffinityProgramStats.legendRedirects,
      color: COLORS.redirect,
    },
  ];

  const chartSeries = seriesDefs
    .filter((s) => visibleSeries[s.key])
    .map((s) => ({
      data: seriesData[s.key],
      label: s.label,
      color: s.color,
    }));

  const computePct = (num: number, denom: number): number | null =>
    denom > 0 ? (num / denom) * 100 : null;

  const memberOpenConversionPct = computePct(
    stats?.totalMemberOpenCount ?? 0,
    stats?.totalMemberViewCount ?? 0
  );
  const nonMemberOpenConversionPct = computePct(
    stats?.totalNonMemberOpenCount ?? 0,
    stats?.totalNonMemberViewCount ?? 0
  );
  const memberRedirectConversionPct = computePct(
    stats?.totalMemberRedirectCount ?? 0,
    stats?.totalMemberOpenCount ?? 0
  );
  const nonMemberRedirectConversionPct = computePct(
    stats?.totalNonMemberRedirectCount ?? 0,
    stats?.totalNonMemberOpenCount ?? 0
  );

  const tableRows = useMemo(() => {
    return allPoints.map((p) => {
      const opensConversion = computePct(p.open, p.views);
      const redirectsConversion = computePct(p.redirect, p.open);
      return {
        month: p.date.format("MMM YYYY"),
        viewsTotal: p.views,
        opensTotal: p.open,
        opensConversion: formatPercent(opensConversion),
        redirectsTotal: p.redirect,
        redirectsConversion: formatPercent(redirectsConversion),
      };
    });
  }, [allPoints]);

  if (!programId) {
    return (
      <div className={classes.pageState}>
        <p>{enumStrings.viewAffinityProgramStats.noProgramSelected}</p>
        <Button
          variant="contained"
          className={classes.stateButton}
          onClick={() => history.goBack()}
        >
          {enumStrings.viewAffinityProgramStats.back}
        </Button>
      </div>
    );
  }

  if ((partnerLoading && !partnerData) || (statsLoading && !statsData)) {
    return (
      <div className={classes.pageRoot}>
        <Loader />
      </div>
    );
  }

  if (partnerError || statsError || !partner || !stats) {
    return (
      <div className={classes.pageState}>
        <p>{enumStrings.viewAffinityProgramStats.loadError}</p>
        <Button
          variant="contained"
          className={classes.stateButton}
          onClick={() => {
            void refetchPartner();
            void refetchStats();
          }}
        >
          {enumStrings.viewAffinityProgramStats.retry}
        </Button>
      </div>
    );
  }

  return (
    <div className={classes.pageRoot}>
      <div className={classes.headerSection}>
        <div className={classes.titleRow}>
          <div>
            <h1 className={classes.title}>
              {enumStrings.viewAffinityProgramStats.pageTitle.replace(
                "{{programName}}",
                programName
              )}
            </h1>
            <p className={classes.description}>
              {enumStrings.viewAffinityProgramStats.pageDescription}
            </p>
          </div>
        </div>
        <div className={classes.buttonContainer}>
          <VideoTutorialButton
            label={enumStrings.viewAffinityProgramStats.watchTutorials}
            tutorialUrl=""
          />
          <ExportButton
            customOnClick={() => undefined}
            disablePadding
            icon={
              <InfoOutlinedIcon
                style={{
                  color: theme.palette.primary.main,
                  marginRight: "10px",
                  width: "24px",
                  height: "24px",
                }}
              />
            }
            label={enumStrings.viewAffinityProgramStats.viewInstructions}
          />
        </div>
      </div>

      <div className={classes.contentContainer}>
        <div className={classes.content}>
          <div className={classes.metricsGrid}>
            <div className={classes.metricCard}>
              <h3 className={classes.metricTitle}>
                {enumStrings.viewAffinityProgramStats.totalViewsTitle}
              </h3>
              <p className={classes.metricDesc}>
                {enumStrings.viewAffinityProgramStats.totalViewsDescription}
              </p>
              <p className={classes.metricValue}>
                {(stats?.totalViewCount ?? 0).toLocaleString()}
              </p>
              <div className={classes.metricBreakdown}>
                <div className={classes.metricRow}>
                  <span>{enumStrings.viewAffinityProgramStats.members}</span>
                  <span className={classes.metricRowValue}>
                    {(stats?.totalMemberViewCount ?? 0).toLocaleString()}
                  </span>
                </div>
                <div className={classes.metricRow}>
                  <span>{enumStrings.viewAffinityProgramStats.nonMembers}</span>
                  <span className={classes.metricRowValue}>
                    {(stats?.totalNonMemberViewCount ?? 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <div className={classes.metricCard}>
              <h3 className={classes.metricTitle}>
                {enumStrings.viewAffinityProgramStats.totalOpensTitle}
              </h3>
              <p className={classes.metricDesc}>
                {enumStrings.viewAffinityProgramStats.totalOpensDescription}
              </p>
              <div className={classes.conversionRow}>
                <p className={classes.metricValue}>
                  {(stats?.totalOpenCount ?? 0).toLocaleString()}
                </p>
                <span className={classes.conversionStack}>
                  <span className={classes.conversionLabel}>
                    {enumStrings.viewAffinityProgramStats.conversionRate}
                  </span>
                  <span className={classes.conversionValue}>
                    {formatPercent(
                      stats?.totalOpensFromViewsConversionRatePercentage
                    )}
                  </span>
                </span>
              </div>
              <div className={classes.metricBreakdown}>
                <div className={classes.metricRow}>
                  <span>
                    {enumStrings.viewAffinityProgramStats.memberOpens}
                  </span>
                  <span className={classes.metricRowValue}>
                    {(stats?.totalMemberOpenCount ?? 0).toLocaleString()}
                  </span>
                </div>
                <div className={`${classes.metricRow} ${classes.metricRowGreen}`}>
                  <span>
                    {enumStrings.viewAffinityProgramStats.memberConversionRate}
                  </span>
                  <span className={classes.metricRowGreenValue}>
                    {formatPercent(memberOpenConversionPct)}
                  </span>
                </div>
                <div className={classes.metricRow}>
                  <span>
                    {enumStrings.viewAffinityProgramStats.nonMemberOpens}
                  </span>
                  <span className={classes.metricRowValue}>
                    {(stats?.totalNonMemberOpenCount ?? 0).toLocaleString()}
                  </span>
                </div>
                <div className={`${classes.metricRow} ${classes.metricRowGreen}`}>
                  <span>
                    {
                      enumStrings.viewAffinityProgramStats
                        .nonMemberConversionRate
                    }
                  </span>
                  <span className={classes.metricRowGreenValue}>
                    {formatPercent(nonMemberOpenConversionPct)}
                  </span>
                </div>
              </div>
            </div>

            <div className={classes.metricCard}>
              <h3 className={classes.metricTitle}>
                {enumStrings.viewAffinityProgramStats.totalRedirectsTitle}
              </h3>
              <p className={classes.metricDesc}>
                {enumStrings.viewAffinityProgramStats.totalRedirectsDescription}
              </p>
              <div className={classes.conversionRow}>
                <p className={classes.metricValue}>
                  {(stats?.totalRedirectCount ?? 0).toLocaleString()}
                </p>
                <span className={classes.conversionStack}>
                  <span className={classes.conversionLabel}>
                    {enumStrings.viewAffinityProgramStats.conversionRate}
                  </span>
                  <span className={classes.conversionValue}>
                    {formatPercent(
                      stats?.totalRedirectCountFromOpensConversionRatePercentage
                    )}
                  </span>
                </span>
              </div>
              <div className={classes.metricBreakdown}>
                <div className={classes.metricRow}>
                  <span>{enumStrings.viewAffinityProgramStats.members}</span>
                  <span className={classes.metricRowValue}>
                    {(stats?.totalMemberRedirectCount ?? 0).toLocaleString()}
                  </span>
                </div>
                <div className={`${classes.metricRow} ${classes.metricRowGreen}`}>
                  <span>
                    {
                      enumStrings.viewAffinityProgramStats
                        .redirectConversionRate
                    }
                  </span>
                  <span className={classes.metricRowGreenValue}>
                    {formatPercent(memberRedirectConversionPct)}
                  </span>
                </div>
                <div className={classes.metricRow}>
                  <span>{enumStrings.viewAffinityProgramStats.nonMembers}</span>
                  <span className={classes.metricRowValue}>
                    {(stats?.totalNonMemberRedirectCount ?? 0).toLocaleString()}
                  </span>
                </div>
                <div className={`${classes.metricRow} ${classes.metricRowGreen}`}>
                  <span>
                    {
                      enumStrings.viewAffinityProgramStats
                        .redirectConversionRate
                    }
                  </span>
                  <span className={classes.metricRowGreenValue}>
                    {formatPercent(nonMemberRedirectConversionPct)}
                  </span>
                </div>
              </div>
            </div>

            <div className={classes.metricCard}>
              <h3 className={classes.metricTitle}>
                {enumStrings.viewAffinityProgramStats.totalInquiriesTitle}
              </h3>
              <p className={classes.metricDesc}>
                {enumStrings.viewAffinityProgramStats.totalInquiriesDescription}
              </p>
              <p className={classes.metricValue}>
                {stats.displayInquirySubmissionCount.toLocaleString()}
              </p>
            </div>
          </div>

          {!stats.inquirySubmissionCountIsDemo ? (
          <section className={classes.inquirySection}>
            <h3 className={classes.inquiryTitle}>
              {enumStrings.viewAffinityProgramStats.inquiriesTitle}
            </h3>
            <p className={classes.inquiryDescription}>
              {enumStrings.viewAffinityProgramStats.inquiriesDescription}
            </p>
            {inquiries.length === 0 ? (
              <div className={classes.emptyState}>
                {enumStrings.viewAffinityProgramStats.inquiriesEmpty}
              </div>
            ) : (
              <div className={classes.tableWrap}>
                <table className={classes.inquiryTable}>
                  <thead>
                    <tr>
                      <th className={classes.inquiryTh}>
                        {enumStrings.viewAffinityProgramStats.inquiryDate}
                      </th>
                      <th className={classes.inquiryTh}>
                        {enumStrings.viewAffinityProgramStats.inquiryName}
                      </th>
                      <th className={classes.inquiryTh}>
                        {enumStrings.viewAffinityProgramStats.inquiryEmail}
                      </th>
                      <th className={classes.inquiryTh}>
                        {enumStrings.viewAffinityProgramStats.inquiryEmployer}
                      </th>
                      <th className={classes.inquiryTh}>
                        {enumStrings.viewAffinityProgramStats.inquiryDeliveryStatus}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {inquiries.map((inquiry) => (
                      <tr key={inquiry.id}>
                        <td className={classes.inquiryTd}>
                          {dayjs(inquiry.createdAtUtcMilli).format(
                            "MMM D, YYYY h:mm A"
                          )}
                        </td>
                        <td className={classes.inquiryTd}>{inquiry.fullName}</td>
                        <td className={classes.inquiryTd}>
                          <a href={`mailto:${inquiry.email}`}>{inquiry.email}</a>
                        </td>
                        <td className={classes.inquiryTd}>
                          {inquiry.employerName}
                        </td>
                        <td className={classes.inquiryTd}>
                          <span
                            className={`${classes.statusBadge} ${inquiryStatusClass(
                              inquiry.emailDeliveryStatus
                            )}`}
                          >
                            {inquiryStatusLabel(inquiry.emailDeliveryStatus)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className={classes.inquiryFooter}>
                  <p className={classes.inquiryCount}>
                    {enumStrings.viewAffinityProgramStats.inquiriesShowing
                      .replace(
                        "{{shown}}",
                        stats.inquiriesReturnedCount.toLocaleString()
                      )
                      .replace(
                        "{{total}}",
                        stats.totalInquirySubmissionCount.toLocaleString()
                      )}
                  </p>
                  {stats.inquiriesNextToken ? (
                    <Button
                      variant="contained"
                      className={classes.stateButton}
                      disabled={loadingMoreInquiries}
                      onClick={() => void loadMoreInquiries()}
                    >
                      {loadingMoreInquiries
                        ? enumStrings.viewAffinityProgramStats
                            .inquiriesLoadingMore
                        : enumStrings.viewAffinityProgramStats
                            .inquiriesLoadMore}
                    </Button>
                  ) : null}
                </div>
                {inquiryLoadMoreFailed ? (
                  <p className={classes.inquiryLoadMoreError} role="alert">
                    {
                      enumStrings.viewAffinityProgramStats
                        .inquiriesLoadMoreError
                    }
                  </p>
                ) : null}
              </div>
            )}
          </section>
          ) : null}

          <div className={classes.chartCard}>
            <h3 className={classes.chartTitle}>
              {enumStrings.viewAffinityProgramStats.chartTitle}
            </h3>
            <div className={classes.chartControlsRow}>
              <div className={classes.legendsRow}>
                {seriesDefs.map((s) => {
                  const isOn = visibleSeries[s.key];
                  return (
                    <span
                      key={s.key}
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleSeries(s.key)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleSeries(s.key);
                        }
                      }}
                      className={`${classes.legendChip} ${
                        isOn ? "" : classes.legendChipInactive
                      }`}
                    >
                      <span
                        className={`${classes.legendCheck} ${
                          isOn ? "" : classes.legendCheckEmpty
                        }`}
                      >
                        {isOn ? "✓" : ""}
                      </span>
                      <span
                        className={classes.legendDot}
                        style={{ backgroundColor: s.color }}
                      />
                      {s.label}
                    </span>
                  );
                })}
              </div>
              <Tabs
                value={range}
                onChange={(_, val) => setRange(val as RangeTab)}
                indicatorColor="primary"
                textColor="primary"
                className={classes.rangeTabs}
              >
                <Tab
                  value="months"
                  label={enumStrings.viewAffinityProgramStats.rangeMonths}
                />
                <Tab
                  value="year"
                  label={enumStrings.viewAffinityProgramStats.rangeYear}
                />
                <Tab
                  value="inception"
                  label={enumStrings.viewAffinityProgramStats.rangeInception}
                />
              </Tabs>
            </div>
            <div className={classes.chartDivider} />
            <div style={{ width: "100%", height: 420 }}>
              <LineChart
                xAxis={[{ data: xLabels, scaleType: "point" }]}
                series={chartSeries}
                height={420}
                margin={{ left: 56, right: 24, top: 24, bottom: 56 }}
                slotProps={{
                  legend: { hidden: true },
                }}
                sx={{
                  "& .MuiChartsAxis-tickLabel": {
                    fontFamily: "DM Sans, sans-serif",
                    fill: "#615E83 !important",
                  },
                  "& .MuiChartsAxis-bottom .MuiChartsAxis-tickLabel": {
                    fontSize: "14px",
                    fontWeight: 400,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  },
                  "& .MuiChartsAxis-left .MuiChartsAxis-tickLabel": {
                    fontSize: "16px",
                    fontWeight: 400,
                  },
                  "& .MuiChartsAxis-line": {
                    stroke: "#E5E5EF",
                  },
                  "& .MuiChartsAxis-tick": {
                    stroke: "#E5E5EF",
                  },
                  "& .MuiChartsGrid-line": {
                    stroke: "#E5E5EF",
                    strokeDasharray: "0",
                  },
                }}
              />
            </div>
          </div>

          <div className={classes.tableWrap}>
            <table className={classes.table}>
              <colgroup>
                <col style={{ width: "160px" }} />
                <col style={{ width: "140px" }} />
                <col style={{ width: "140px" }} />
                <col style={{ width: "160px" }} />
                <col style={{ width: "140px" }} />
                <col style={{ width: "160px" }} />
              </colgroup>
              <thead>
                <tr>
                  <th className={classes.thGold}>
                    {enumStrings.viewAffinityProgramStats.tableMonth}
                  </th>
                  <th className={classes.thGold}>
                    {enumStrings.viewAffinityProgramStats.tableViews}
                  </th>
                  <th colSpan={2} className={classes.thGold}>
                    {enumStrings.viewAffinityProgramStats.tableOpens}
                  </th>
                  <th colSpan={2} className={classes.thGold}>
                    {enumStrings.viewAffinityProgramStats.tableRedirects}
                  </th>
                </tr>
                <tr>
                  <th className={classes.thSub} aria-hidden="true" />
                  <th className={classes.thSub}>
                    {enumStrings.viewAffinityProgramStats.tableTotal}
                  </th>
                  <th className={classes.thSub}>
                    {enumStrings.viewAffinityProgramStats.tableTotal}
                  </th>
                  <th className={`${classes.thSub} ${classes.thSubGreen}`}>
                    {enumStrings.viewAffinityProgramStats.tableOverallConversion}
                  </th>
                  <th className={classes.thSub}>
                    {enumStrings.viewAffinityProgramStats.tableTotal}
                  </th>
                  <th className={`${classes.thSub} ${classes.thSubGreen}`}>
                    {enumStrings.viewAffinityProgramStats.tableOverallConversion}
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row) => (
                  <tr key={row.month}>
                    <td className={`${classes.td} ${classes.tdMonth}`}>
                      {row.month}
                    </td>
                    <td className={`${classes.td} ${classes.tdTotal}`}>
                      {row.viewsTotal.toLocaleString()}
                    </td>
                    <td className={`${classes.td} ${classes.tdTotal}`}>
                      {row.opensTotal.toLocaleString()}
                    </td>
                    <td className={`${classes.td} ${classes.tdGreen}`}>
                      {row.opensConversion}
                    </td>
                    <td className={`${classes.td} ${classes.tdTotal}`}>
                      {row.redirectsTotal.toLocaleString()}
                    </td>
                    <td className={`${classes.td} ${classes.tdGreen}`}>
                      {row.redirectsConversion}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewAffinityProgramStats;
